import { useEffect, useState } from 'react';
import KeyBusinessMetricsPanel from './KeyBusinessMetricsPanel';
import MarketingPerformancePanel from './MarketingPerformancePanel';
import moment from 'moment';
import { useSelector } from 'react-redux';
import useModal from '../../../hooks/modalHook';
import useUserAccount from '../../../hooks/userAccountHook';
import { IGeneralMetrics } from '../../../models/generalMetrics/IGeneralMetrics';
import { CURRENCIES } from '../../../resources/enums';
import { IActivityStreams, ITask, ISalesMetrics, ISalesChartData } from '../../../resources/interfaces';
import { getSelectedCountry } from '../../../states/slices/resourceSlice';
import { getCurrentQuarter, getGreeting } from '../../../utils/miscFunctions';
import { SelectedDateRange } from '../date-range-selector/datePicker';
import { IUser, User } from '../../../models/User';
import { getAllTasks, getCompanyStatistics, getDashboardGenericsDataSuper, getDashboardGenericsGraphData, getDashboardMarketingMetrics, getStaffActivities, getSupervisorSurbodinates } from '../../../api/requests';
import { useQuery } from '@tanstack/react-query';
import DashboardSidebarContent from '../../../views/shared/dashboard/shared/revenueTarget/SidebarContent';
import CompanyStatisticsPanel, { ICompanyStatistics } from './CompanyStatisticsPanel';
import UserStatisticsPanel from './UserStatisticsPanel';
import useBroker from '../../../hooks/brokerHook';
import IssuanceModeMetrics from './IssuanceModeMetrics';
import WorkspaceSetupBanner from '../../ui/WorkspaceSetupBanner';
import { getWorkspaceSetupBannerMinimized } from '../../../states/slices/globalSlice';

const SuperAdminDashboard = () => {
  const { user } = useUserAccount();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [supervisorSubordinates, setSupervisorSubordinates] = useState<User[]>();
  const [staffActivities, setStaffActivities] = useState<IActivityStreams[] | null>(null);
  const [myTasks, setMyTasks] = useState<ITask[]>();
  const selectedCountry = useSelector(getSelectedCountry);
  let countryId = selectedCountry?.id ?? 1
  const countryIso = selectedCountry?.iso ?? 'GH'
  const isMinimized = useSelector(getWorkspaceSetupBannerMinimized);

  const [selectedDateRange, setSelectedDateRange] = useState<SelectedDateRange>({
    from: '',
    to: ''
  });

  const [splineChartRange, setSplineChartRange] = useState({
    quarter: `Q${getCurrentQuarter()}`,
    year: moment().format('YYYY')
  });

  const [salesChartData, setSalesChartData] = useState<ISalesChartData>({
    policy_count: {
      '1': 0,
      '2': 0,
      '3': 0,
      '4': 0,
      '5': 0,
      '6': 0,
      '7': 0,
      '8': 0,
      '9': 0,
      '10': 0,
      '11': 0,
      '12': 0,
      '13': 0
    },
    policy_sales: {
      '1': 0,
      '2': 0,
      '3': 0,
      '4': 0,
      '5': 0,
      '6': 0,
      '7': 0,
      '8': 0,
      '9': 0,
      '10': 0,
      '11': 0,
      '12': 0,
      '13': 0
    },
    month_on_month_sales: {
      January: 0,
      February: 0,
      March: 0,
      April: 0,
      May: 0,
      June: 0,
      July: 0,
      August: 0,
      September: 0,
      October: 0,
      November: 0,
      December: 0
    },
    month_on_month_growth: {
      January: 0,
      February: 0,
      March: 0,
      April: 0,
      May: 0,
      June: 0,
      July: 0,
      August: 0,
      September: 0,
      October: 0,
      November: 0,
      December: 0
    },
    quarterly_growth: {
      January: null,
      February: null,
      March: 0,
      April: null,
      May: null,
      June: 0,
      July: null,
      August: null,
      September: 0,
      October: null,
      November: null,
      December: 0
    },
    week_on_week_sales: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 0,
      7: 0,
      8: 0,
      9: 0,
      10: 0,
      11: 0,
      12: 0,
      13: 0
    }
  });

  const [generalMetrics, setGeneralMetrics] = useState<IGeneralMetrics>({
    arpc: 0,
    cac: 0,
    churn: 0,
    csatScore: 0,
    averageRevenue: 0,
    referralRate: 0,
    nsm: 0,
    conversionRate: 0,
    grossMargin: 0,
    mmr: 0,
    pcr: 0,
    totalRevenue: 0,
    leads: 0,
    payments: 0,
    policy: 0,
    sales: 0,
    customers: 0,
    customerCount: 0,
    ltv: 0,
    rgr: 0,
    gmv: 0,
    arr: 0,
    roms: 0,
    cpl: 0,
    leadCount: 0,
    policyCount: 0,
    leads_channel: {
      bolt: 0,
      yango: 0,
      whatsapp: 0,
      referral: 0,
      facebook: 0,
      direct: 0,
      telegram: 0,
      google: 0,
      instagram: 0,
      radio: 0
    },
    customer_channel: {
      whatsapp: 0,
      web: 0,
      facebook: 0,
      telegram: 0,
      google: 0,
      instagram: 0
    },
    sales_channel: {
      whatsapp: 0,
      web: 0,
      facebook: 0,
      telegram: 0,
      google: 0,
      instagram: 0
    },
    leads_status: {
      warm: 0,
      paid: 0,
      closed: 0,
      cold: 0
    },
    payment_amount_upcoming_this_week: 0,
    total_outstanding_payments: 0,
    all_overdue_payment_amount: 0,
    payment_amount_due_this_month: 0,
    payment_amount_due_this_week: 0,
    payment_amount_due_today: 0,
    sales_mix: {
      amount: {
        comprehensive_90: 0,
        comprehensive_85: 0,
        comprehensive_100: 0,
        'third-party-ft': 0,
        'third-party': 0
      },
      count: {
        comprehensive_90: 0,
        comprehensive_85: 0,
        comprehensive_100: 0,
        'third-party-ft': 0,
        'third-party': 0
      }
    },
    leads_tags: {
      CONTACTED: 0,
      CONVERTED: 0,
      DISQUALIFIED: 0,
      IN_NEGOTIATION: 0,
      NEW: 0,
      NURTURING: 0,
      QUALIFIED: 0,
      UNRESPONSIVE: 0
    }
  });

  const [salesMix, setSalesMix] = useState({
    amount: {
      Comprehensive: 10,
      'Third Party': 30
    },
    count: {
      Comprehensive: 40,
      'Third Party': 50
    }
  });

  const [salesMetrics, setSalesMetrics] = useState<ISalesMetrics>({
    gmv: 0,
    revenue: 0,
    rpc: 0,
    cac: 0,
    prevGmv: 0,
    prevRevenue: 0,
    prevRpc: 0,
    prevCac: 0
  });

  const countryCurrency = () => {
    switch (countryIso) {
      case 'GH':
        return CURRENCIES.GHC;
      case 'NG':
        return CURRENCIES.NGN;
      default:
        break;
    }
  }

  const [currency, setCurrency] = useState<any>(countryCurrency());

  useEffect(() => {
    setCurrency(countryCurrency())
  }, [countryId])

  const handleSetSalesMix = () => {
    if (generalMetrics?.sales_mix) {
      setSalesMix({
        ...salesMix,
        amount: {
          Comprehensive:
            generalMetrics?.sales_mix.amount.comprehensive_100 +
            generalMetrics?.sales_mix.amount.comprehensive_90 +
            generalMetrics?.sales_mix.amount.comprehensive_85,
          'Third Party':
            generalMetrics?.sales_mix.amount['third-party'] +
            generalMetrics?.sales_mix.amount['third-party-ft']
        },
        count: {
          Comprehensive:
            generalMetrics?.sales_mix.count.comprehensive_100 +
            generalMetrics?.sales_mix.count.comprehensive_90 +
            generalMetrics?.sales_mix.count.comprehensive_85,
          'Third Party':
            generalMetrics?.sales_mix.count['third-party'] +
            generalMetrics?.sales_mix.count['third-party-ft']
        }
      });
    }
  }

  const getMarketingMetrics = async () => {
    if (user.isOwner()) {
      try {
        const res = await getDashboardMarketingMetrics(countryId, selectedDateRange);


        if (res) {
          setSalesMetrics({
            gmv: Number(res?.gmv.toFixed(2)),
            revenue: Number(res?.revenue.toFixed(2)),
            rpc: Number(res?.rpc.toFixed(2)),
            cac: Number(res?.cac.toFixed(2)),
            prevGmv: Number(res?.prevGmv.toFixed(2)),
            prevRevenue: Number(res?.prevRevenue.toFixed(2)),
            prevRpc: Number(res?.prevRpc.toFixed(2)),
            prevCac: Number(res?.prevCac.toFixed(2))
          });
        }

        setCurrency(CURRENCIES.GHC);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const getSalesChartData = async () => {
    try {
      const res = await getDashboardGenericsGraphData(countryId, splineChartRange);
      setSalesChartData(res);
    } catch (err) {
      console.error(err);
    }
  };

  const getGeneralMetricsData = async () => {
    try {
      const res = await getDashboardGenericsDataSuper(countryId, selectedDateRange);
      if (res) {
        setGeneralMetrics(res);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getSupervisorStaff = async () => {
    try {
      const res = await getSupervisorSurbodinates();
      const _staff = res?.map((staff: IUser) => new User(staff));
      setSupervisorSubordinates(_staff);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchStaffActivities = async () => {
    try {
      const res: IActivityStreams[] = await getStaffActivities();
      setStaffActivities(res);
    } catch (error) {
      console.error(error);
    }
  };

  const getMyTasks = async () => {
    try {
      const res = await getAllTasks(0, 100);
      setMyTasks(res?.content);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCompanyStatistics = async () => {
    try {
      const response = await getCompanyStatistics(countryId);
      return response as ICompanyStatistics;
    } catch (error) {
      console.error('Error fetching company statistics:', error);
      return null;
    }
  };

  useQuery({ queryKey: ['salesChartData', splineChartRange, countryId], queryFn: getSalesChartData });
  useQuery({ queryKey: ['marketingMetrics', selectedDateRange, countryId], queryFn: getMarketingMetrics });
  useQuery({ queryKey: ['generalMetrics', selectedDateRange, countryId], queryFn: getGeneralMetricsData });
  useQuery({ queryKey: ["handleSetSalesMix", generalMetrics], queryFn: handleSetSalesMix })
  useQuery({ queryKey: ['myTasks'], queryFn: getMyTasks });

  const { data: companyStatistics, isLoading: isLoadingCompanyStatistics } = useQuery({
    queryKey: ['companyStatistics', countryId],
    queryFn: () => fetchCompanyStatistics(),
  });

  const { isLoading: subordinatesLoading } = useQuery({
    queryKey: ['supervisorSubordinates'],
    queryFn: getSupervisorStaff
  });

  const { isLoading: staffActivityLoading } = useQuery({
    queryKey: ['staffActivities'],
    queryFn: fetchStaffActivities
  });

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  return (
    <div className="space-y-8 mt-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{getGreeting()}, {user?.firstName}</h1>
        <p className="text-gray-600 dark:text-gray-300">{new Date().toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
        </p>
      </div>

      {/* <div className={`${isMinimized ? '' : 'flex-col lg:flex-row flex gap-5'}`}> */}
        <div className="space-y-8 w-full">
          {generalMetrics && (
            <>
              {/* Key Business Metrics Section */}
              <KeyBusinessMetricsPanel metrics={generalMetrics} country={countryIso} />

              {/* Marketing Performance Panel */}
              {/* <MarketingPerformancePanel data={generalMetrics} country={countryIso} /> */}
              {/* Insurance Company Issuance Mode Metrics */}

              {/* Policy Analytics Panel */}
              {/* <PolicyAnalyticsPanel chartData={salesChartData} generalMetrics={generalMetrics} country={countryIso} /> */}
            </>
          )}

          <IssuanceModeMetrics />
          {/* Finance & Payments Panel */}
          {/* <FinancePaymentsPanel data={paymentMetrics} country={countryIso} /> */}

          {/* <RevenueChart country={selectedCountry?.iso} /> */}

          {/* Company Statistics Panel */}
          <CompanyStatisticsPanel data={companyStatistics} country={countryIso} isLoadingCompanyStatistics={isLoadingCompanyStatistics} />

          <UserStatisticsPanel data={companyStatistics} isLoadingUserStatistics={isLoadingCompanyStatistics} />

          <div className="relative">

            {/* Toggle button */}
            <button
              onClick={toggleDrawer}
              className="fixed right-0 top-1/2 z-30 p-2 bg-primary-main text-white rounded-l-lg shadow-lg hover:bg-primary-main transition-colors"
              style={{ transform: 'translateY(-50%)' }}
            >
              {isDrawerOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              )}
            </button>

            <DashboardSidebarContent />
          </div>
        </div>

        {/* Show full banner in sidebar when not minimized */}
        {/* {!isMinimized && (
          <div className="w-full lg:w-80 xl:w-96 flex-shrink-0">
            <WorkspaceSetupBanner />
          </div>
        )}
      </div> */}
    </div>
  );
};

export default SuperAdminDashboard;
